import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import Marquee from "../components/Marquee";
import { Reveal, RevealStagger, RevealItem } from "../components/Reveal";

const services_preview = [
  { n: "01", k: "web", t_cs: "Web & E-commerce", t_en: "Web & E-commerce", span: "lg:col-span-7" },
  { n: "02", k: "apps", t_cs: "Webové aplikace", t_en: "Web apps", span: "lg:col-span-5" },
  { n: "03", k: "bots", t_cs: "Telegram boti & chatboti", t_en: "Telegram & chatbots", span: "lg:col-span-4" },
  { n: "04", k: "auto", t_cs: "Automatizace procesů", t_en: "Process automation", span: "lg:col-span-4" },
  { n: "05", k: "int", t_cs: "Integrace & API", t_en: "Integrations & API", span: "lg:col-span-4" },
  { n: "06", k: "ux", t_cs: "UI / UX design", t_en: "UI / UX design", span: "lg:col-span-6" },
  { n: "07", k: "ads", t_cs: "Reklama & SEO", t_en: "Ads & SEO", span: "lg:col-span-6" },
];

const Home = () => {
  const { t, lang } = useLang();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] lg:min-h-screen pt-28 md:pt-32 pb-16 md:pb-20 px-5 md:px-10 overflow-hidden">
        {/* Side label */}
        <div className="hidden xl:flex absolute left-6 top-1/2 -translate-y-1/2">
          <span className="vertical-text">Terwilliger · 2026 · Praha</span>
        </div>

        <div className="max-w-[1480px] mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-end">
            {/* Kicker */}
            <div className="lg:col-span-12 mb-8 md:mb-14">
              <Reveal>
                <span className="tag" data-testid="hero-kicker">
                  {t("home.hero_kicker")}
                </span>
              </Reveal>
            </div>

            {/* H1 — asymmetric */}
            <div className="lg:col-span-9 lg:col-start-1">
              <h1 className="h-display" data-testid="hero-title">
                <motion.span
                  className="block"
                  initial={{ y: 60, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
                >
                  {t("home.hero_title_a")}
                </motion.span>
                <motion.span
                  className="block ml-[4vw] md:ml-[10vw]"
                  initial={{ y: 60, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.1, ease: [0.2, 0.8, 0.2, 1] }}
                >
                  {t("home.hero_title_b")}
                </motion.span>
                <motion.span
                  className="block text-[#E5FF00]"
                  initial={{ y: 60, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
                >
                  {t("home.hero_title_c")}
                </motion.span>
              </h1>
            </div>

            {/* Side info card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="lg:col-span-4 lg:col-start-9 self-end mt-2 lg:mt-0"
            >
              <div className="border-l border-white/15 pl-5 md:pl-6">
                <p className="text-zinc-300 text-base md:text-lg leading-relaxed mb-6 md:mb-8">
                  {t("home.hero_lede")}
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/contact" className="btn-acid" data-testid="hero-cta-start">
                    {t("home.cta_start")}
                    <ArrowUpRight size={16} strokeWidth={2.5} />
                  </Link>
                  <Link to="/services" className="btn-ghost" data-testid="hero-cta-services">
                    {t("home.cta_services")}
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Floor — scroll hint + stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8 mt-16 md:mt-32 pt-8 md:pt-12 border-t border-white/10">
            {t("home.stats").map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div>
                  <p className="font-display text-3xl md:text-5xl text-[#E5FF00]">
                    {s.v}
                  </p>
                  <p className="text-zinc-500 text-[11px] md:text-sm mt-2 uppercase tracking-[0.18em] leading-snug">
                    {s.l}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3 mt-12 md:mt-16 text-zinc-500">
            <ArrowDown size={18} className="animate-bounce" />
            <span className="font-mono-tech text-xs uppercase tracking-[0.3em]">scroll</span>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee items={t("home.marquee_items")} />

      {/* SERVICES — TETRIS */}
      <section className="px-6 md:px-10 py-24 md:py-36" data-testid="home-services-section">
        <div className="max-w-[1480px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 md:mb-20">
            <div className="lg:col-span-3">
              <Reveal>
                <span className="tag">{t("home.services_kicker")}</span>
              </Reveal>
            </div>
            <div className="lg:col-span-8 lg:col-start-5">
              <Reveal delay={0.1}>
                <h2 className="h-display-md" data-testid="home-services-title">
                  {t("home.services_title")}
                  <span className="block text-[#E5FF00]">
                    {t("home.services_title_b")}
                  </span>
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="text-zinc-400 text-lg max-w-2xl mt-8">
                  {t("home.services_lede")}
                </p>
              </Reveal>
            </div>
          </div>

          <RevealStagger className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-5">
            {services_preview.map((s, idx) => (
              <RevealItem key={s.n} className={`md:col-span-6 ${s.span} group`}>
                <Link
                  to="/services"
                  className="tetris-card block h-full min-h-[180px] md:min-h-[260px]"
                  data-testid={`home-service-${s.n}`}
                >
                  <div className="flex items-start justify-between mb-10 md:mb-20">
                    <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#E5FF00]">
                      {s.n} /
                    </span>
                    <ArrowUpRight
                      size={28}
                      className="text-zinc-600 group-hover:text-[#E5FF00] transition-all duration-300 group-hover:rotate-45"
                      strokeWidth={1.5}
                    />
                  </div>
                  <p className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight">
                    {lang === "cs" ? s.t_cs : s.t_en}
                  </p>
                </Link>
              </RevealItem>
            ))}
            <RevealItem className="md:col-span-12 lg:col-span-6">
              <Link
                to="/services"
                className="block h-full min-h-[120px] md:min-h-[140px] border border-[#E5FF00] bg-[#E5FF00] text-black p-7 md:p-10 group transition-colors hover:bg-transparent hover:text-[#E5FF00]"
                data-testid="home-services-all"
              >
                <div className="flex items-center justify-between h-full gap-4">
                  <span className="font-display text-2xl md:text-5xl tracking-tight">
                    {lang === "cs" ? "Všechny služby" : "All services"}
                  </span>
                  <ArrowUpRight size={36} strokeWidth={1.5} className="md:w-12 md:h-12 group-hover:rotate-45 transition-transform shrink-0" />
                </div>
              </Link>
            </RevealItem>
          </RevealStagger>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="px-5 md:px-10 py-20 md:py-36 border-t border-white/10" data-testid="home-about-teaser">
        <div className="max-w-[1480px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <Reveal className="lg:col-span-5 lg:col-start-1">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1758520145147-c30bc656f314?crop=entropy&cs=srgb&fm=jpg&w=900&q=80"
                alt="Studio at night"
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between">
                <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-white/70">
                  Praha · Studio
                </span>
                <span className="text-[#E5FF00] font-display text-2xl">2026</span>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <span className="tag">{t("home.about_kicker")}</span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="h-display-md mt-6" data-testid="home-about-title">
                {t("home.about_title_a")}
                <span className="block text-[#E5FF00]">
                  {t("home.about_title_b")}
                </span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-zinc-400 text-lg mt-8 leading-relaxed">
                {t("home.about_lede")}
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link
                to="/about"
                className="inline-flex items-center gap-3 mt-10 font-mono-tech text-sm uppercase tracking-[0.25em] text-[#E5FF00] link-underline"
                data-testid="home-about-link"
              >
                {t("home.about_link")}
                <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA BLOCK */}
      <section className="px-5 md:px-10 py-20 md:py-36" data-testid="home-cta-section">
        <div className="max-w-[1480px] mx-auto">
          <div className="border border-white/10 p-7 md:p-16 lg:p-24 bg-[#0a0a0a] relative overflow-hidden">
            <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-[#E5FF00] opacity-[0.06] blur-3xl pointer-events-none" />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 relative">
              <div className="lg:col-span-2">
                <Reveal>
                  <span className="tag">{t("home.cta_block_kicker")}</span>
                </Reveal>
              </div>
              <div className="lg:col-span-7">
                <Reveal delay={0.1}>
                  <h2 className="h-display-md">
                    {t("home.cta_block_title_a")}
                    <span className="block text-[#E5FF00]">
                      {t("home.cta_block_title_b")}
                    </span>
                  </h2>
                </Reveal>
              </div>
              <div className="lg:col-span-3 flex flex-col justify-end">
                <Reveal delay={0.2}>
                  <p className="text-zinc-400 mb-6">{t("home.cta_block_lede")}</p>
                  <Link to="/contact" className="btn-acid" data-testid="home-cta-block-btn">
                    {t("home.cta_start")}
                    <ArrowUpRight size={16} strokeWidth={2.5} />
                  </Link>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
